const PGAdapter = require('./../../common/pg-adapter')

const DATA_SHIPMENTSNAPSHOT_TABLE_NAME = 'avocado.data_shipmentsnapshot'
const DATA_SHIPMENTSNAPSHOT_COLUMNS = [
  'id',
  'shipment_id',
  'shipment_number',
  'status',
  'route_id',
  'funder_id',
  'program_id',
  'vendor_id',
  'origin_id',
  'destination_id',
  'notes',
  'is_automatic_return',
  'parent_doc_id',
  'order_id',
  'created_at',
  'updated_at',
  'created_by',
  'couchdb_id',
  'suborder_id'
]

const DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME = 'avocado.data_shipmentsnapshot_change'

class ShipmentSnapshotAdapter extends PGAdapter {
  constructor (pgConnection, username, logger) {
    super(
      pgConnection,
      DATA_SHIPMENTSNAPSHOT_TABLE_NAME,
      username,
      DATA_SHIPMENTSNAPSHOT_COLUMNS,
      'id',
      null,
      logger,
      { strictColumns: false }
    )
  }

  /**
   * Get CouchDB IDs and location IDs for shipment snapshots by route IDs
   * @param {string[]} routeIds - Array of route IDs to filter by
   * @param {object} options - Query options
   * @param {string[]} [options.programIds] - Filter by program IDs
   * @param {string} [options.cutoffDate] - ISO date string (e.g., '2024-01-01T00:00:00.000Z')
   * @returns {Promise<{couchdbIds: string[], locationIds: string[]}>} Object with couchdbIds and unique locationIds (origins + destinations)
   */
  async getCouchdbIdsByRoutes (routeIds, options = {}) {
    if (!Array.isArray(routeIds)) {
      throw new Error('routeIds must be an array')
    }
    if (routeIds.length === 0) {
      return { couchdbIds: [], locationIds: [], funderIds: [], serviceIds: [], productIds: [] }
    }

    const { programIds = [], cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let query = `
      SELECT ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.couchdb_id IS NOT NULL
        AND ss.route_id = ANY($${paramIndex++}::text[])
    `

    params.push(routeIds)

    if (programIds.length > 0) {
      query += ` AND ss.program_id = ANY($${paramIndex++}::text[])`
      params.push(programIds)
    }

    if (cutoffDate) {
      query += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    // We add the Group by clause after adding potential filters
    query += ` GROUP BY ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, s.fsid`

    try {
      const result = await this.pgConnection.query(query, params)

      const couchdbIds = result.rows.map(row => row.couchdb_id)
      // Dedupe location IDs because multiple shipments may share the same origin/destination.
      // These IDs are used for offline locationMatch filtering in the van-shipments dispenser.
      const locationSet = new Set()
      const skuSet = []
      const funderSet = new Set()
      const serviceSet = new Set()

      result.rows.forEach(row => {
        if (row.origin_id) locationSet.add(row.origin_id)
        if (row.destination_id) locationSet.add(row.destination_id)
        if (row.skus && Array.isArray(row.skus)) {
          skuSet.push(...row.skus)
        }
        if (row.funder_id) funderSet.add(row.funder_id)
        if (row.service_id) serviceSet.add(row.service_id)
      })

      return {
        couchdbIds,
        locationIds: Array.from(locationSet),
        funderIds: Array.from(funderSet),
        serviceIds: Array.from(serviceSet),
        productIds: [...new Set(skuSet)]
      }
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-query-failed',
          routeIds,
          programIds,
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Get CouchDB IDs, location IDs, funder IDs, service IDs, product IDs, and change doc IDs
   * for shipment snapshots grouped by route ID.
   * Single query that returns per-route data for the cache producer Lambda.
   * Matches the same JOINs and output fields as getCouchdbIdsByRoutes.
   * @param {object} options - Query options
   * @param {string} [options.cutoffDate] - ISO date string - only return snapshots updated after this date
   * @returns {Promise<Object<string, {couchdbIds: string[], locationIds: string[], funderIds: string[], serviceIds: string[], productIds: string[], changeDocIds: string[]}>>} Map of routeId -> route data
   */
  async getCouchdbIdsByRoutesGrouped (options = {}) {
    const { cutoffDate = null } = options

    const params = []
    let paramIndex = 1

    let snapshotQuery = `
      SELECT ss.route_id,
             ss.couchdb_id,
             ss.origin_id,
             ss.destination_id,
             ss.funder_id,
             ss.updated_at,
             s.fsid as service_id,
             ARRAY_AGG('product:' || ssl.sku) FILTER (WHERE ssl.sku IS NOT NULL) AS skus
      FROM ${DATA_SHIPMENTSNAPSHOT_TABLE_NAME} ss
      LEFT JOIN avocado.data_shipmentsnapshotline ssl ON ss.id = ssl.shipment_snapshot_id
      LEFT JOIN avocado.data_program p ON ss.program_id = p.fsid
      LEFT JOIN avocado.data_service s ON p.id = s.program_id
      WHERE ss.route_id IS NOT NULL
        AND ss.couchdb_id IS NOT NULL
    `

    if (cutoffDate) {
      snapshotQuery += ` AND ss.updated_at >= $${paramIndex++}`
      params.push(cutoffDate)
    }

    snapshotQuery += ` GROUP BY ss.route_id, ss.couchdb_id, ss.origin_id, ss.destination_id, ss.funder_id, ss.updated_at, s.fsid`

    try {
      const snapshotResult = await this.pgConnection.query(snapshotQuery, params)

      // Group snapshots by route
      const routeMap = {}
      const allCouchdbIds = []

      for (const row of snapshotResult.rows) {
        if (!routeMap[row.route_id]) {
          routeMap[row.route_id] = {
            couchdbIds: new Set(),
            locationIds: new Set(),
            funderIds: new Set(),
            serviceIds: new Set(),
            productIds: new Set(),
            lastUpdated: row.updated_at
          }
        }
        const route = routeMap[row.route_id]
        route.couchdbIds.add(row.couchdb_id)
        if (row.origin_id) route.locationIds.add(row.origin_id)
        if (row.destination_id) route.locationIds.add(row.destination_id)
        if (row.funder_id) route.funderIds.add(row.funder_id)
        if (row.service_id) route.serviceIds.add(row.service_id)
        if (row.skus && Array.isArray(row.skus)) {
          row.skus.forEach(sku => route.productIds.add(sku))
        }
        if (row.updated_at > route.lastUpdated) route.lastUpdated = row.updated_at
        allCouchdbIds.push(row.couchdb_id)
      }

      // Batch fetch change docs for all snapshots
      const changeDocIds = allCouchdbIds.length > 0
        ? await this._getChangeDocsBySnapshot(allCouchdbIds)
        : {}

      // Build final result
      const result = {}
      for (const [routeId, data] of Object.entries(routeMap)) {
        const couchdbIds = Array.from(data.couchdbIds)
        result[routeId] = {
          couchdbIds,
          locationIds: Array.from(data.locationIds),
          funderIds: Array.from(data.funderIds),
          serviceIds: Array.from(data.serviceIds),
          productIds: Array.from(data.productIds),
          changeDocIds: couchdbIds.flatMap(id => changeDocIds[id] || []),
          lastUpdated: data.lastUpdated
        }
      }

      return result
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-snapshot-grouped-query-failed',
          cutoffDate,
          error: error.message
        })
      }
      throw error
    }
  }

  /**
   * Internal helper: get change doc IDs grouped by their parent snapshot CouchDB ID.
   * @param {string[]} snapshotCouchdbIds
   * @returns {Promise<Object<string, string[]>>} Map of snapshotCouchdbId -> changeDocCouchdbIds
   */
  async _getChangeDocsBySnapshot (snapshotCouchdbIds) {
    const query = `
      SELECT sc.snapshot_couchdb_id, sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
    const grouped = {}
    for (const row of result.rows) {
      if (!grouped[row.snapshot_couchdb_id]) grouped[row.snapshot_couchdb_id] = []
      grouped[row.snapshot_couchdb_id].push(row.couchdb_id)
    }
    return grouped
  }

  /**
   * Get CouchDB IDs for change documents linked to the specified snapshots
   * @param {string[]} snapshotCouchdbIds - Array of snapshot CouchDB IDs
   * @returns {Promise<string[]>} Array of change document CouchDB IDs
   */
  async getChangeDocIds (snapshotCouchdbIds) {
    if (!Array.isArray(snapshotCouchdbIds) || snapshotCouchdbIds.length === 0) {
      return []
    }

    const query = `
      SELECT sc.couchdb_id
      FROM ${DATA_SHIPMENTSNAPSHOT_CHANGE_TABLE_NAME} sc
      WHERE sc.snapshot_couchdb_id = ANY($1::text[])
    `

    try {
      const result = await this.pgConnection.query(query, [snapshotCouchdbIds])
      return result.rows.map(row => row.couchdb_id)
    } catch (error) {
      if (this.logger) {
        this.logger.error({
          code: 'shipment-change-query-failed',
          snapshotCouchdbIds,
          error: error.message
        })
      }
      throw error
    }
  }
}

module.exports = ShipmentSnapshotAdapter
